import React from 'react';
import './StepWizard.css';

export type StepWizardInfoItem = {
  label: string;
  value: React.ReactNode;
};

type StepWizardProps = {
  steps: string[];
  currentStep: number;
  onStepChange: (step: number) => void;
  infoItems?: StepWizardInfoItem[];
  showActions?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  disablePrevious?: boolean;
  disableNext?: boolean;
  previousLabel?: string;
  nextLabel?: string;
};

const StepWizard: React.FC<StepWizardProps> = ({
  steps,
  currentStep,
  onStepChange,
  infoItems = [],
  showActions = false,
  onPrevious,
  onNext,
  disablePrevious,
  disableNext,
  previousLabel = 'Anterior',
  nextLabel = 'Próximo'
}) => {
  const totalSteps = Math.max(1, steps.length);
  const safeCurrentStep = Math.min(totalSteps, Math.max(1, currentStep));
  const progressRatio = totalSteps > 1 ? (safeCurrentStep - 1) / (totalSteps - 1) : 0;
  const edgeInsetPercent = 50 / totalSteps;

  const handlePrevious = () => {
    if (onPrevious) {
      onPrevious();
      return;
    }
    onStepChange(Math.max(1, safeCurrentStep - 1));
  };

  const handleNext = () => {
    if (onNext) {
      onNext();
      return;
    }
    onStepChange(Math.min(totalSteps, safeCurrentStep + 1));
  };

  return (
    <>
      <div className="swz-row">
        {infoItems.length > 0 && (
          <div className="swz-info-card">
            {infoItems.map((item) => (
              <div key={item.label}>
                <strong>{item.label}</strong> {item.value}
              </div>
            ))}
          </div>
        )}

        <div className="swz-main">
          <div className="swz-track">
            <button
              type="button"
              className="swz-nav-btn"
              onClick={handlePrevious}
              disabled={disablePrevious ?? safeCurrentStep === 1}
              aria-label={previousLabel}
              title={previousLabel}
            >
              <svg className="swz-nav-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M14.5 5.5L8 12l6.5 6.5" />
              </svg>
            </button>

            <div className="swz-steps" style={{ ['--swz-edge-inset' as any]: `${edgeInsetPercent}%` }}>
              <div className="swz-line swz-line-base" />
              <div
                className="swz-line swz-line-progress"
                style={{ transform: `scaleX(${progressRatio})` }}
              />

              {steps.map((label, index) => {
                const stepNumber = index + 1;
                const isDone = stepNumber < safeCurrentStep;
                const isActive = stepNumber === safeCurrentStep;

                return (
                  <button
                    key={label}
                    type="button"
                    className={`swz-step ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}
                    onClick={() => onStepChange(stepNumber)}
                  >
                    <span className="swz-circle">{stepNumber}</span>
                    <span className="swz-label">{label}</span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="swz-nav-btn"
              onClick={handleNext}
              disabled={disableNext ?? safeCurrentStep === totalSteps}
              aria-label={nextLabel}
              title={nextLabel}
            >
              <svg className="swz-nav-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M9.5 5.5L16 12l-6.5 6.5" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {showActions && (
        <div className="swz-actions d-flex justify-content-end mt-3" style={{ marginBottom: 8 }}>
          <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={handlePrevious}
            disabled={disablePrevious ?? safeCurrentStep === 1}
          >
            {previousLabel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleNext}
            disabled={disableNext ?? safeCurrentStep === totalSteps}
          >
            {nextLabel}
          </button>
        </div>
      )}
    </>
  );
};

export default StepWizard;
